import { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { breakpoints, fontStyle, Modal, palette, spacing } from '@playdapp/ui';

import { useAppDispatch } from 'store/hooks';
import { clearTxStatus } from 'store/marketplace';
import { clearTxStatus as clearSolanaTxStatus } from 'store/marketplaceSolana';
import useWindowWidth from 'hooks/useWindowWidth';
import viewports from 'data/viewports';
import { STATUS_KEY, contents, steps } from 'data/transactionModal';
import { blockExplorerUrl } from 'lib/environment';
import type { StepTypeSubFix } from 'types/transactionModal';
import type { NetworkName } from 'types/network';

import Button from '@/components/Button';
import Step from '@/components/Step';
import TransactionResultModal from '@/components/Modal/TransactionResultModal';

type Props = {
  status: string | null;
  currency?: string;
  stepTypeSubFix?: StepTypeSubFix;
  network?: NetworkName;
  txHash?: string | null;
  isOpen: boolean;
  isShowSteps?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  handleOpen: (isOpen: boolean) => void;
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 ${spacing.m};

  ${breakpoints.down('lg')} {
    flex-direction: column;
    padding: 0;
  }
`;

const ContentsBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${({ theme }) => theme.rem(586)};

  ${breakpoints.down('lg')} {
    width: 100%;
    margin-top: ${spacing.xxl};
  }
`;

const ButtonBlock = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing.xxl};

  ${breakpoints.down('lg')} {
    justify-content: center;
  }
`;

const Heading = styled.div`
  margin-bottom: ${spacing.l};
  color: ${palette.black};
  ${fontStyle('h3')}

  ${breakpoints.down('lg')} {
    text-align: center;
  }
`;

const Body = styled.div`
  color: ${palette.gray900};
  ${fontStyle('p1')}

  ${breakpoints.down('lg')} {
    text-align: center;
  }
`;

const TransactionModal = ({
  status,
  currency,
  stepTypeSubFix = '',
  network,
  txHash,
  isOpen,
  isShowSteps = true,
  shouldCloseOnOverlayClick = false,
  handleOpen,
}: Props) => {
  const [modalWidth, setModalWidth] = useState('800px');
  const { width, handleResize } = useWindowWidth();
  const dispatch = useAppDispatch();

  const data = useMemo(() => {
    if (!status || status === STATUS_KEY.TX_FAILED) return null;

    const stepType =
      stepTypeSubFix === 'transfer'
        ? stepTypeSubFix
        : `${currency}${stepTypeSubFix}`;

    const stepInfo = steps[stepType];

    let step = 1;

    if (
      status === STATUS_KEY.APPROVE_ALL_ASSETS ||
      status === STATUS_KEY.CREATE_ORDER_MAKE_OFFER ||
      status === STATUS_KEY.FULFILL_ORDER_BUY
    ) {
      step = 2;
    } else if (
      stepTypeSubFix !== 'Buy' &&
      (status === STATUS_KEY.APPROVE_CURRENCY_PLA ||
        status === STATUS_KEY.APPROVE_CURRENCY_WETH ||
        status === STATUS_KEY.APPROVE_CURRENCY_KLAY ||
        status === STATUS_KEY.TRANSFER_ALL ||
        status === STATUS_KEY.TRANSFER_ONE ||
        status === STATUS_KEY.WRAP_ETH)
    ) {
      step = 3;

      if (stepTypeSubFix === 'MakeOffer') {
        step = 1;
      }
    } else if (
      status === STATUS_KEY.CREATE_ORDER_LISTING ||
      status === STATUS_KEY.FULFILL_ORDER_OFFER_ACCEPT
    ) {
      step = 4;

      if (
        stepType === 'matic' ||
        stepType === 'eth' ||
        stepType === 'sol' ||
        stepType === 'klay'
      ) {
        step = 3;
      }
    }

    const borderSize = width < viewports.LARGE ? 140 : 80;
    const stepBarSize =
      width < viewports.MEDIUM && stepInfo && stepInfo.length > 1
        ? `${(step - 1) * (100 / (stepInfo.length - 1))}%`
        : `${(step - 1) * borderSize}px`;

    return {
      contents: status ? contents[status] : null,
      stepInfo,
      step,
      stepBarSize,
    };
  }, [width, status, currency, stepTypeSubFix]);

  const handleClearTxStatus = () => {
    dispatch(clearTxStatus());
    dispatch(clearSolanaTxStatus());
  };

  useEffect(() => {
    if (width < viewports.MEDIUM) {
      setModalWidth('100%');
    } else if (width < viewports.LARGE) {
      setModalWidth('500px');
    } else {
      setModalWidth('800px');
    }
  }, [width]);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  if (status === STATUS_KEY.TX_FAILED) {
    return (
      <TransactionResultModal
        isOpen={status === STATUS_KEY.TX_FAILED}
        handleOpen={handleClearTxStatus}
        type="error"
        txHash={null}
      />
    );
  }

  if (!data?.contents || !data?.stepInfo || !data?.step || !data?.stepBarSize) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      hideCloseButton
      width={modalWidth}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      handleOpen={handleOpen}
    >
      <Container>
        {isShowSteps && (
          <Step
            step={data.step}
            stepInfo={data.stepInfo}
            stepBarSize={data.stepBarSize}
          />
        )}

        <ContentsBlock>
          <Heading>{data.contents.heading}</Heading>
          <Body>
            {data.contents.body.map((text, item) => (
              <p key={`text-${item}`}>{text}</p>
            ))}
          </Body>
          {txHash &&
            network &&
            status === STATUS_KEY.APPROVE_ALL_ASSETS &&
            network !== 'solana' && (
              <ButtonBlock>
                <Button
                  to={`${
                    blockExplorerUrl[
                      process.env.NEXT_PUBLIC_ENV as
                        | 'development'
                        | 'production'
                    ][network]
                  }/tx/${txHash}`}
                >
                  View Transaction
                </Button>
              </ButtonBlock>
            )}
        </ContentsBlock>
      </Container>
    </Modal>
  );
};

export default TransactionModal;
